<template>
    <!-- Trigger either dialog or bottom sheet -->
    <VJoyDialogTrigger v-if="isDesktopScreen" :dialog="changeOwnerDialogRef">
        <VJoyIconButton size="small" variant="ghost" icon="pen" data-testid="open-change-project-owner-overlay-button" />
    </VJoyDialogTrigger>
    <VJoyBottomSheetTrigger v-else :bottom-sheet="changeOwnerBottomSheetRef">
        <VJoyIconButton size="small" variant="ghost" icon="pen" data-testid="open-change-project-owner-overlay-button" />
    </VJoyBottomSheetTrigger>
    <!-- Dialog component -->
    <VJoyDialog
        ref="changeOwnerDialogRef"
        data-testid="change-project-owner-overlay"
        :cancel-text="t('generic.form.cancel.label')"
        size="large"
        @dialog:hide="handleResetValues"
    >
        <template #dialog-header>
            <h3>{{ t('projectclient.projectview.sidepanel.team.dialog.search.label', {0: companyName}) }}</h3>
        </template>
        <template #dialog-body>
            <OverlayContent
                v-model:new-identity-id="newIdentityId"
                v-model:invitation-email="invitationEmail"
                v-model:search-query="searchQuery"
                :current-project-owner="currentProjectOwner"
                :filtered-members="filteredMembers"
                :error="r$.invitationEmail.$errors[0]"
            />
        </template>
        <template #dialog-confirm>
            <VJoyButton
                variant="main"
                size="small"
                :disabled="!newIdentityId && !invitationEmail"
                data-testid="confirm-project-owner-change-button"
                @click="onRequestOwnerChange"
            >
                {{ t('generic.form.confirm.label') }}
            </VJoyButton>
        </template>
    </VJoyDialog>
    <!-- Bottomsheet component -->
    <VJoyBottomSheet ref="changeOwnerBottomSheetRef" data-testid="change-project-owner-overlay" @bottom-sheet:close="handleResetValues">
        <template #bottom-sheet-content>
            <h3 class="joy-font-primary-650">{{ t('projectclient.projectview.sidepanel.team.dialog.search.label', {0: companyName}) }}</h3>
            <OverlayContent
                v-model:new-identity-id="newIdentityId"
                v-model:invitation-email="invitationEmail"
                v-model:search-query="searchQuery"
                :current-project-owner="currentProjectOwner"
                :filtered-members="filteredMembers"
                :error="r$.invitationEmail.$errors[0]"
            />
        </template>
        <template #bottom-sheet-cta>
            <div class="bottom-sheet__footer">
                <VJoyButton
                    variant="main"
                    size="small"
                    :disabled="!newIdentityId && !invitationEmail"
                    data-testid="confirm-project-owner-change-button"
                    @click="onRequestOwnerChange"
                >
                    {{ t('generic.form.confirm.label') }}
                </VJoyButton>
            </div>
        </template>
    </VJoyBottomSheet>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef, watch} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import type {Maybe} from '@regle/core';
    import {email, isFilled, withMessage} from '@regle/rules';
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useAuth, useDataTracking, useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyBottomSheet, VJoyBottomSheetTrigger, VJoyButton, VJoyDialog, VJoyDialogTrigger, VJoyIconButton} from '@maltjoy/core-vue';
    import OverlayContent from './OverlayContent.vue';
    import {useCompanyMembersStore} from '../../stores';

    const props = defineProps<{
        currentProjectOwner: string;
    }>();

    const {t} = useTranslation();
    const {track} = useDataTracking();

    const route = useRoute('project-projectId-index');

    const auth = useAuth();
    const {getSelectedIdentity} = storeToRefs(auth);
    const companyName = computed(() => auth.getSelectedIdentity?.clientCompanyName);

    // companyMembers store
    const companyMembersStore = useCompanyMembersStore();
    const {companyMembers} = storeToRefs(companyMembersStore);

    // screen size store
    const screenSizeStore = useScreenSizeStore();
    const {isDesktopScreen} = storeToRefs(screenSizeStore);

    // dialog + bottom sheet refs
    const changeOwnerDialogRef = useTemplateRef('changeOwnerDialogRef');
    const changeOwnerBottomSheetRef = useTemplateRef('changeOwnerBottomSheetRef');

    function closeModals() {
        changeOwnerDialogRef.value?.hide();
        changeOwnerBottomSheetRef.value?.hide();
    }

    // company members list
    const filteredMembers = computed(() => {
        return (
            companyMembers.value &&
            companyMembers.value.filter((member) => {
                const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
                return fullName.includes(searchQuery.value.toLowerCase()) && fullName !== props.currentProjectOwner.toLowerCase();
            })
        );
    });

    // handle update project owner
    const newIdentityId = ref<string>('');
    const invitationEmail = ref<string>('');
    const searchQuery = ref('');

    async function onRequestOwnerChange() {
        if (newIdentityId.value) {
            await companyMembersStore.updateProjectOwner({newIdentityId: newIdentityId.value}, route.params.projectId);
            closeModals();
        } else if (invitationEmail.value) {
            const {valid} = await validateForm(r$);
            if (!valid) {
                return;
            }

            await companyMembersStore.updateProjectOwner({invitationEmail: invitationEmail.value}, route.params.projectId);
            track('client_team_invitation_sent', {
                client_invitation_flow: 'project-manager-changed-dialog',
                url: window.location.pathname,
                client_invitation_type: 'company_email',
                number_emails_added_to_invitation: 1,
                company_id: getSelectedIdentity.value?.clientCompanyId,
            });
            closeModals();
        }
    }

    function handleResetValues() {
        newIdentityId.value = '';
        invitationEmail.value = '';
        searchQuery.value = '';
        r$.$reset();
    }

    watch(invitationEmail, (newVal) => {
        if (newVal !== '') {
            newIdentityId.value = '';
        }
    });

    watch(newIdentityId, (newVal) => {
        if (newVal !== '') {
            invitationEmail.value = '';
        }
    });

    // handle email input validation
    function emailAlreadyRegistered(newEmail: string): boolean {
        return companyMembers.value?.some((member) => member.email === newEmail) || false;
    }

    const {r$} = useRegle(
        {invitationEmail},
        {
            invitationEmail: {
                email: withMessage(email, t('projectclient.projectview.sidepanel.team.dialog.email.validation.wrong.format')),
                emailUnique: withMessage((value: Maybe<string>) => {
                    if (isFilled(value)) {
                        return !emailAlreadyRegistered(value);
                    }
                    return true;
                }, t('projectclient.projectview.sidepanel.team.dialog.email.validation.already.registered')),
            },
        },
        {rewardEarly: true},
    );
</script>

<style lang="scss" scoped>
    .bottom-sheet {
        &__footer {
            width: 100%;
            display: flex;

            button {
                flex: 1;
            }
        }
    }
</style>
